import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';
import Button from 'Components/Atoms/Button/Button';
import { useQueryClient } from '@tanstack/react-query';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import FormProvider, { RHFInput } from 'Components/Organisms/HookForm';
import { useForm } from 'react-hook-form';
import { deleteTransaction } from 'Services/transactions';
import deleteImage from 'assets/icon-delete.svg';

const magicWords = {
    ES: 'ELIMINAR',
    EN: 'DELETE',
};

export default function TransactionDeleteDrawer(props) {
    const { transaction, onCancel, onSubmit, onError } = props;

    const queryClient = useQueryClient();

    const { t, i18n } = useTranslation('transactions');
    const magicWord = magicWords[i18n.language] || magicWords.EN;

    const schema = yup.object().shape({
        captcha: yup
            .string()
            .required()
            .transform((_value, originalValue) => originalValue.toUpperCase().trim())
            .oneOf([magicWord], t('invalid-magic-word', { magicWord })),
    });

    const methods = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            captcha: '',
        },
    });

    const {
        handleSubmit,
        formState: { isSubmitting },
    } = methods;

    const handleDelete = async () => {
        try {
            await deleteTransaction(transaction.id);
            queryClient.invalidateQueries({ queryKey: ['dashboard'] });
            queryClient.invalidateQueries({ queryKey: ['transactions'] });
            onSubmit();
        } catch (error) {
            onError(error);
        }
    };

    return (
        <FormProvider methods={methods} onSubmit={handleSubmit(handleDelete)} className="flex flex-col h-full">
            <div className="mt-6 mb-6">
                <div className="text-center">
                    <div className="mb-4 flex justify-center">
                        <img src={deleteImage} alt="" />
                    </div>
                    <div className="text-lg font-semibold mb-1 text-dark-blue">
                        <Trans t={t} i18nKey="delete-transaction">
                            ¿Eliminar
                            <span className="text-primary">{{ description: transaction.description }}</span>?
                        </Trans>
                    </div>
                    <div className="mb-8 text-gray-500 text-sm">{t('delete-transaction-description')}</div>
                </div>

                <ul className="mb-8 list-disc pl-6  text-gray-500">
                    <li className="mb-1">{t('delete-transaction-note-1')}</li>
                    <li className="font-semibold mb-1">{t('delete-transaction-note-2')}</li>
                    <li className="mb-1">{t('delete-transaction-note-3')}</li>
                </ul>
                <div className="py-3">
                    <RHFInput data-autofocus="true" label={t('write-magic-word', { magicWord })} name="captcha" />
                </div>
            </div>

            <div className="grow" />

            <div className="sticky bottom-0 bg-white pt-2 pb-4 z-10">
                <div className="flex gap-2">
                    <Button
                        isBusy={isSubmitting}
                        variant="primary"
                        className="grow"
                        type="submit"
                        testId="transaction-delete.delete"
                    >
                        {t('delete')}
                    </Button>
                    <Button
                        variant="default"
                        className="grow"
                        onClick={onCancel}
                        testId="transaction-delete.cancel"
                    >
                        {t('cancel')}
                    </Button>
                </div>
            </div>
        </FormProvider>
    );
}

TransactionDeleteDrawer.propTypes = {
    onClose: PropTypes.func,
    transaction: PropTypes.object.isRequired,
    onCancel: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onError: PropTypes.func.isRequired,
};

TransactionDeleteDrawer.drawerStackProps = {
    size: 'lg',
};
